/**
 * Lookup Content Service
 *
 * Fetches the courier-managed city and spoken-language catalogs from the
 * haat-hub portal-backend public endpoints. This is a SEPARATE backend from
 * the Courier Connect backend that `apiClient` targets, so it uses its own
 * base URL (`REACT_APP_PORTAL_API_BASE_URL`) - the same one used by
 * `welcomeContentService`.
 *
 * Each entry carries he/ar/en labels; callers localize via `localizeCity` /
 * `localizeLanguage`. The English value (`nameEn` / `langEn`) is the canonical
 * value submitted to the backend so Monday sync keeps matching its English
 * dropdown labels.
 */

import axios from 'axios';
import { resolveLanUrl } from 'shared/lib/resolveLanUrl';
import { getRuntimeEnv } from 'shared/lib/runtimeConfig';
import type { SupportedLanguage } from 'shared/i18n/i18n';
import type { VehicleType } from 'features/courier-onboarding/types';

const PORTAL_BASE_URL = resolveLanUrl(
  getRuntimeEnv('REACT_APP_PORTAL_API_BASE_URL') || 'http://localhost:3117',
);

const CITIES_ENDPOINT = '/api/v1/courier-connect/public/cities';
const LANGUAGES_ENDPOINT = '/api/v1/courier-connect/public/languages';

const portalClient = axios.create({
  baseURL: PORTAL_BASE_URL,
  withCredentials: false,
  timeout: 15_000,
  headers: { Accept: 'application/json' },
});

/**
 * Area category a city belongs to. Drives which registration tracks are
 * offered during onboarding:
 * - EXPANSION: only the Tax ID (licensed business) track.
 * - OPERATION: both the finance company and Tax ID tracks.
 */
export type CcCityAreaType = 'EXPANSION' | 'OPERATION';

/** Vehicle types shown when a city has no explicit configuration. */
export const DEFAULT_ALLOWED_VEHICLES: VehicleType[] = ['MOTORCYCLE', 'CAR'];

export interface CcCity {
  id: number;
  nameEn: string;
  nameAr: string;
  nameHe: string;
  areaType?: CcCityAreaType;
  allowedVehicleTypes?: VehicleType[] | null;
}

/**
 * Resolve a city's area type, defaulting to OPERATION (both tracks) when it is
 * missing - e.g. fallback cities or a catalog row created before area typing.
 */
export const cityAreaType = (city: CcCity | undefined): CcCityAreaType =>
  city?.areaType ?? 'OPERATION';

/**
 * Resolve the vehicle types offered for a city. Falls back to the default set
 * (Motorcycle + Car) when the city has none configured or is unknown.
 */
export const cityAllowedVehicles = (
  city: CcCity | undefined,
): VehicleType[] =>
  city?.allowedVehicleTypes && city.allowedVehicleTypes.length > 0
    ? city.allowedVehicleTypes
    : DEFAULT_ALLOWED_VEHICLES;

// Bundled fallback used when the portal-backend catalog is unreachable. Names
// are English-only (same value for all locales) so Monday sync still matches.
const mkFallbackCity = (id: number, name: string): CcCity => ({
  id,
  nameEn: name,
  nameAr: name,
  nameHe: name,
  areaType: 'OPERATION',
  allowedVehicleTypes: DEFAULT_ALLOWED_VEHICLES,
});

export const FALLBACK_CITIES: CcCity[] = [
  mkFallbackCity(-1, 'Acre - Nahariya'),
  mkFallbackCity(-2, 'Afula'),
  mkFallbackCity(-3, 'Al-Sharawiya'),
  mkFallbackCity(-4, 'Baqa al-Gharbiyye'),
  mkFallbackCity(-5, 'Haifa'),
];

export interface CcLanguage {
  id: number;
  langEn: string;
  langAr: string;
  langHe: string;
}

type Wrapped<T> = { data?: T } | T;

const unwrap = <T>(payload: Wrapped<T>): T => {
  if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
    return (payload as { data: T }).data;
  }
  return payload as T;
};

/**
 * Fetch the active city catalog. Throws on network/HTTP errors so callers can
 * fall back to a bundled list.
 */
export const fetchManagedCities = async (): Promise<CcCity[]> => {
  const { data } = await portalClient.get<Wrapped<CcCity[]>>(CITIES_ENDPOINT);
  return unwrap(data) ?? [];
};

/**
 * Fetch the spoken-language catalog. Throws on network/HTTP errors so callers
 * can fall back to a bundled list.
 */
export const fetchManagedLanguages = async (): Promise<CcLanguage[]> => {
  const { data } = await portalClient.get<Wrapped<CcLanguage[]>>(LANGUAGES_ENDPOINT);
  return unwrap(data) ?? [];
};

/** Pick the city label for the given UI language, defaulting to English. */
export const localizeCity = (city: CcCity, lang: SupportedLanguage): string => {
  if (lang === 'he') return city.nameHe || city.nameEn;
  if (lang === 'ar') return city.nameAr || city.nameEn;
  return city.nameEn;
};

/** Pick the language label for the given UI language, defaulting to English. */
export const localizeLanguage = (
  language: CcLanguage,
  lang: SupportedLanguage,
): string => {
  if (lang === 'he') return language.langHe || language.langEn;
  if (lang === 'ar') return language.langAr || language.langEn;
  return language.langEn;
};
